// useUserRole.ts hooks for roles

import { useMemo, useCallback } from 'react'
import { useUserProfile } from './useUserProfile'
import { ROLES } from '../consts/roles'
import { type UserRole } from '../models/UserRoles'
import { isViewerFamilyRole } from '../helpers/roleHelpers'

/* =======================================================
   Types
======================================================= */

type RoleCheckFn = (role: ROLES) => boolean
type MultiRoleCheckFn = (...roles: ROLES[]) => boolean

type GeneratedRoleFlags = {
  [K in ROLES as `is${Capitalize<
  K extends `${infer First} ${infer Rest}`
    ? `${First}${Rest}`
    : K
  >}`]: boolean
}

export interface UseUserRoleReturn extends GeneratedRoleFlags {
  /** True while profile is loading */
  isLoading: boolean

  /** Currently active role data */
  activeRole: UserRole | undefined

  /** Currently active role ID */
  activeRoleId: string | null

  /** All roles assigned to user */
  assignedRoles: ROLES[]

  /** True if the active role has GLOBAL or GENERIC scope */
  isGlobalOrGenericRole: boolean

  /** True if the ACTIVE role has GLOBAL scope only */
  isGlobalRole: boolean

  /**
   * True if the user holds ANY role with GLOBAL scope, whichever role is active.
   * Distinct from `isGlobalRole`, which reads only the active role: a user who
   * holds Account Owner but has switched to Owner is `hasGlobalRole` but not
   * `isGlobalRole`. Sandbox visibility (CR-341) uses this one, so switching
   * roles does not hide a sandbox project from the person who owns it.
   */
  hasGlobalRole: boolean

  /** True if user has exact role */
  hasRole: RoleCheckFn

  /** True if user matches at least one role */
  hasAnyRole: MultiRoleCheckFn

  /** True if user matches all roles */
  hasAllRoles: MultiRoleCheckFn

  /** True if user matches none of roles */
  hasNoneRole: MultiRoleCheckFn

  /** Change active role */
  setActiveRole: (roleId: string) => void
}

/* =======================================================
   Constants
======================================================= */

const STORAGE_KEY = 'currentRoleId'

/* =======================================================
   Helpers
======================================================= */

const formatRoleToFlag = (role: string): string =>
  'is' +
  role
    .replace(/\s+/g, '')
    .replace(/^\w/, c => c.toUpperCase())

const getStoredRoleId = (): string | null => {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(STORAGE_KEY)
}

/* =======================================================
   Hook
======================================================= */

export const useUserRole = (): UseUserRoleReturn => {
  const { data: userProfile, isLoading } = useUserProfile()

  /* -----------------------------------
     Resolve Active Role
  ----------------------------------- */

  const { activeRole, activeRoleId, assignedRoles } = useMemo(() => {
    if (!userProfile?.roles?.length) {
      return {
        activeRole: undefined,
        activeRoleId: null,
        assignedRoles: []
      }
    }

    const storedRoleId = getStoredRoleId()

    const matchedRole = userProfile.roles.find(
      role => role.roleId === storedRoleId
    )

    const resolvedRole = matchedRole ?? userProfile.roles[0]

    return {
      activeRole: resolvedRole,
      activeRoleId: resolvedRole.roleId,
      assignedRoles: userProfile.roles.map(
        r => r.roleName as ROLES
      )
    }
  }, [userProfile])

  /* -----------------------------------
     Role Utility Functions
  ----------------------------------- */

  const hasRole = useCallback<RoleCheckFn>(
    role => {
      if (role === ROLES.VIEWER) {
        return isViewerFamilyRole(activeRole?.roleName)
      }
      return activeRole?.roleName === role
    },
    [activeRole]
  )

  const hasAnyRole = useCallback<MultiRoleCheckFn>(
    (...roles) => roles.some(role => {
      if (role === ROLES.VIEWER) {
        return isViewerFamilyRole(activeRole?.roleName)
      }
      return activeRole?.roleName === role
    }),
    [activeRole]
  )

  const hasAllRole = useCallback<MultiRoleCheckFn>(
    (...roles) => roles.every(role => role === activeRole?.roleName),
    [activeRole]
  )

  const hasNoneRole = useCallback<MultiRoleCheckFn>(
    (...roles) => activeRole?.roleName == null || !roles.includes(activeRole.roleName as ROLES),
    [activeRole]
  )

  /* -----------------------------------
     Role Switching
  ----------------------------------- */

  const setActiveRole = (roleId: string): void => {
    if (typeof window === 'undefined') return
    localStorage.setItem(STORAGE_KEY, roleId)
    window.location.reload()
  }

  /* -----------------------------------
     Auto-Generated Role Flags
  ----------------------------------- */

  const roleFlags = useMemo(() => {
    return Object.values(ROLES).reduce<Record<string, boolean>>(
      (acc, role) => {
        const flag = formatRoleToFlag(role)
        acc[flag] = activeRole?.roleName === role
        return acc
      },
      {}
    )
  }, [activeRole])

  const isGlobalOrGenericRole = activeRole?.roleType === 'GLOBAL' || activeRole?.roleType === 'GENERIC'
  const isGlobalRole = activeRole?.roleType === 'GLOBAL'
  const hasGlobalRole = userProfile?.roles?.some(role => role.roleType === 'GLOBAL') ?? false

  return {
    isLoading,
    activeRole,
    activeRoleId,
    assignedRoles,
    isGlobalOrGenericRole,
    isGlobalRole,
    hasGlobalRole,

    hasRole,
    hasAnyRole,
    hasAllRoles: hasAllRole,
    hasNoneRole,

    setActiveRole,

    ...(roleFlags as GeneratedRoleFlags)
  }
}
